import { useEffect, useState } from 'react';
import { collection, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Question } from '@/types/questions';
import { useAuth } from "@/contexts/AuthContext";
import { getUserData, setUserPremium } from "@/lib/firebase";
import type { UserData } from "@/lib/firebase";
import PaywallModal from '@/components/PaywallModal';

interface QuestionResponse {
  questions: Array<{
    question: string;
    options: string[];
    answer: string;
    imageUrl?: string;
  }>;
}

interface QuestionsProps {
  questions?: Question[];
}

const Questions = ({ questions: initialQuestions = [] }: QuestionsProps) => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>(initialQuestions);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentSubject] = useState<string>('biology');
  const [currentYear] = useState<string>('2015');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        const data = await getUserData(user.uid);
        setUserData(data);
      }
    };
    fetchUserData();
  }, [user]);

  useEffect(() => {
    let isMounted = true;

    const fetchQuestions = async () => {
      try {
        if (!userData?.isPremium && currentYear !== '2015') {
          setShowPaywall(true);
          return;
        }

        setLoading(true);
        const subjectRef = collection(db, currentSubject.toLowerCase());
        const yearDocRef = doc(subjectRef, currentYear);
        const yearDocSnap = await getDoc(yearDocRef);

        if (yearDocSnap.exists()) {
          const data = yearDocSnap.data() as QuestionResponse;
          const formattedQuestions = data.questions.map((q: any, index: number) => ({
            id: String(index + 1),
            question: q.question || q.text,
            text: q.text,
            options: q.options || [],
            answer: q.answer || '',
            imageUrl: q.imageUrl || '',
            subject: currentSubject,
            year: currentYear
          }));
          if (isMounted) {
            setQuestions(formattedQuestions);
          }
        }
      } catch (error) {
        console.error('Error fetching questions:', error);
        if (isMounted) {
          setError('Failed to load questions. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchQuestions();

    return () => {
      isMounted = false;
    };
  }, [currentSubject, currentYear, userData]);

  const handlePaymentSuccess = async (response: any) => {
    if (!user) return;

    const transaction = {
      reference: response.reference,
      amount: response.amount,
      timestamp: new Date(),
      status: 'success' as const,
      userEmail: user.email || ''
    };

    try {
      await setUserPremium(user.uid, transaction);
      const updatedData = await getUserData(user.uid);
      setUserData(updatedData);
      setShowPaywall(false);
    } catch (error) {
      console.error('Payment error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600">Loading questions...</div>
          <div className="text-sm text-gray-500 mt-2">Please wait</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-xl sm:text-2xl font-bold mb-4">Questions List</h1>
      <div className="space-y-4 sm:space-y-6">
        {questions.map((question) => (
          <div key={question.id} className="border p-4 rounded-lg shadow-sm">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                {question.imageUrl && (
                  <div className="question-image flex-shrink-0">
                    <img 
                      src={question.imageUrl} 
                      alt="Question illustration"
                      className="max-w-[200px] h-auto rounded-lg object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        console.error('Error loading image:', question.imageUrl);
                      }}
                    />
                  </div>
                )}
                <div className="flex-grow">
                  <div className="font-medium text-gray-500 mb-1">
                    Question {question.id}
                  </div>
                  <p className="font-semibold mb-4">{question.question}</p>
                  <ul className="list-disc ml-6 space-y-2">
                    {question.options.map((option, index) => (
                      <li 
                        key={index} 
                        className={`${
                          String.fromCharCode(65 + index) === question.answer 
                            ? 'text-green-600 font-medium' 
                            : 'text-gray-700'
                        }`}
                      >
                        {option}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 text-sm text-gray-500">
                    {question.subject} - {question.year}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {showPaywall && (
        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          onPaymentSuccess={handlePaymentSuccess}
          userEmail={user?.email || ''}
        />
      )}
    </div>
  );
};

export default Questions; 